import { graphql } from '@/graphql-setup'
import { gql } from '@apollo/client'
import { CompanyFragments } from './fragments'

export const CURRENT_COMPANY_QUERY = gql`
  query CurrentCompany {
    currentCompany {
      ...CompanyInfo
      ...CompanyUserTypes
      numberOfUsers
    }
  }
  ${CompanyFragments.info}
  ${CompanyFragments.userTypes}
`

export const CURRENT_COMPANY_SETTINGS_QUERY = gql`
  query CurrentCompanySettings {
    currentCompany {
      ...CompanyInfo
      country
      language
    }
    allCompanies {
      edges {
        node {
          ...CompanyInfo
        }
      }
    }
  }
  ${CompanyFragments.info}
`

export const CURRENT_COMPANY_NOTIFICATION_SETTINGS_QUERY = graphql(`
  query CurrentCompanyNotificationSettings {
    currentCompany {
      id
      notificationSettings {
        id
        name
        sendSms
        sendEmail
        sendInApp
      }
    }
  }
`)

export const ALL_SALARY_MODULE_CONFIGS_QUERY = gql`
  query AllSalaryModuleConfigs {
    allSalaryModuleConfigs {
      edges {
        node {
          id
          name
          active
          moduleOptions {
            id
            name
            active
          }
          responsibleUsers {
            id
            fullName
            initials
            profilePicture
          }
          hasDashboardIntegration
          salaryWorkTimes {
            id
            priority
            dailyWorkLimit
            periodWorkLimit
            pauseDuration
            pauseThreshold
            paidPauseThreshold
            salaryDateStart
            salaryDateEnd
            name
            useSalaryDayPayment
            salaryType
            useWeekOvertime
            usePlannerForOvertime
            usePlannerForUndertime
            undertimeLimit

            useAutomaticDietGeneration
            useAutomaticSalaryDayGeneration

            defaultSalaryCode {
              id
              name
              externalId
            }

            absenceHourTransactionOvertimeSalaryCode {
              id
              name
              externalId
            }

            dayOvertimeSalaryCode {
              id
              name
              externalId
            }

            weekOvertimeSalaryCode {
              id
              name
              externalId
            }

            periodAddons {
              id
              start
              end

              salaryCode {
                id
                name
                externalId
              }

              monday
              tuesday
              wednesday
              thursday
              friday
              saturday
              sunday
            }

            salaryGroups {
              edges {
                node {
                  id
                  name
                }
              }
            }
            defaultDepartment {
              id
              name
            }
            availableDepartments {
              id
              name
            }
          }
        }
      }
    }
  }
`

export const ALL_SALARY_WORK_TIMES_QUERY = graphql(`
  query AllSalaryWorkTimes {
    allSalaryWorkTimes {
      id
      name
    }
  }
`)

export const COMPANY_RULES_QUERY = gql`
  query CompanyRules {
    currentCompany {
      id
      ruleCapabilities {
        trigger
        conditions {
          type
          fields {
            key
            valueType
            required
          }
        }
        effects {
          type
          fields {
            key
            valueType
            required
          }
        }
      }
      rules {
        id
        name
        trigger
        active
        conditions {
          id
          conditionType
          operator
          salaryWorkTime {
            id
            name
          }
          salaryCode {
            id
            name
            externalId
          }
        }
        effects {
          id
          effectType
          amount
          salaryCode {
            id
            name
            externalId
          }
        }
      }
    }
  }
`

export const SALARY_WORK_TIME_QUERY = graphql(`
  query SalaryWorkTime($id: ID!) {
    salaryWorkTime(id: $id) {
      id
      priority
      salaryType
      dailyWorkLimit
      periodWorkLimit
      pauseDuration
      pauseThreshold
      countPaidPauseInTotalWork
      paidPauseThreshold
      subtractPaidPauseAfterTotalWorkThreshold
      paidPauseSalaryCode {
        id
        name
        externalId
      }

      holidayAllowances {
        id
        day
      }

      salaryDateStart
      salaryDateEnd
      name
      groupSalaryOnVehicle
      useSalaryDayPayment
      useWeekOvertime
      calculateWeekOvertimeInline
      addOvertimeAsAddon
      useDayOvertime
      useMonthOvertime
      usePlannerForOvertime
      usePlannerForUndertime
      undertimeLimit
      addOvertimeAsAddon

      useAutomaticDietGeneration
      useAutomaticSalaryDayGeneration

      user {
        id
        fullName
      }
      salaryGroups {
        edges {
          node {
            id
            name
          }
        }
      }
      defaultDepartment {
        id
        name
      }
      availableDepartments {
        id
        name
      }
      defaultSalaryCode {
        id
        name
        externalId
      }
      defaultSalaryCodeWhenOvertime {
        id
        name
        externalId
      }
      absenceHourTransactionOvertimeSalaryCode {
        id
        name
        externalId
      }
      drivingSalaryCode {
        id
        name
        externalId
      }
      otherWorkSalaryCode {
        id
        name
        externalId
      }
      availabilitySalaryCode {
        id
        name
        externalId
      }
      salaryDayCode {
        id
        name
        externalId
      }
      dayOvertimeSalaryCode {
        id
        name
        externalId
      }

      weekOvertimeSalaryCode {
        id
        name
        externalId
      }
      monthOvertimeSalaryCode {
        id
        name
        externalId
      }
      periodAddons {
        __typename
        id
        start
        end

        salaryCode {
          id
          name
          externalId
        }

        excludeIfOvertime
        override

        monday
        tuesday
        wednesday
        thursday
        friday
        saturday
        sunday
      }
      periodOvertimes {
        __typename
        id
        start
        end

        salaryCode {
          id
          name
          externalId
        }

        alwaysActive

        monday
        tuesday
        wednesday
        thursday
        friday
        saturday
        sunday
      }
      overtimeLimits {
        id
        threshold
        salaryCode {
          id
          name
          externalId
        }
      }
      moduleConfig {
        id
        name
        active
        salaryWorkTimes {
          id
          priority
        }
      }
      selectableSalaryCodes {
        id
        name
      }
      undertimeSalaryCodes {
        id
        name
      }
    }
  }
`)

export const MODULE_CONFIG_QUERY = graphql(`
  query ModuleConfig($name: String!) {
    moduleConfig(name: $name) {
      id
      name
      active
      moduleOptions {
        id
        name
        active
        moduleConfig {
          id
          name
        }
      }
      responsibleUsers {
        id
        fullName
        firstName
        lastName
        initials
        profilePicture
      }
      salaryWorkTimes {
        id
        priority
        dailyWorkLimit
        periodWorkLimit
        pauseDuration
        pauseThreshold
        countPaidPauseInTotalWork
        paidPauseThreshold
        salaryDateStart
        salaryDateEnd
        name

        groupSalaryOnVehicle
        useSalaryDayPayment
        useWeekOvertime
        usePlannerForOvertime
        usePlannerForUndertime
        undertimeLimit
        useAutomaticDietGeneration
        useAutomaticSalaryDayGeneration

        defaultSalaryCode {
          id
          name
          externalId
        }

        defaultDepartment {
          id
          name
        }
        availableDepartments {
          id
          name
        }

        periodAddons {
          id

          salaryCode {
            id
            name
            externalId
          }
        }

        salaryGroups {
          edges {
            node {
              id
              name
            }
          }
        }
      }
      hasDashboardIntegration
    }
  }
`)

export const ALL_MODULE_CONFIG_QUERYS_SHALLOW = graphql(`
  query AllModuleConfigsShallow {
    allModuleConfigs {
      id
      name
      active
      moduleOptions {
        id
        name
        active
      }
    }
  }
`)

export const ALL_API_KEYS = gql`
  query AllApiKeys {
    allVolvoApiKeys {
      edges {
        node {
          id
          active
          username
          password
        }
      }
    }
    allScaniaApiKeys {
      edges {
        node {
          id
          active
          fmsAccessKey
          fmsSecretKey
        }
      }
    }
    allFleetboardApiKeys {
      edges {
        node {
          id
          active
          fleet
          username
          password
        }
      }
    }
    allManApiKeys {
      edges {
        node {
          id
          active
          integrationId
        }
      }
    }
  }
`

export const VOLVO_API_KEY = gql`
  query VolvoApiKey($id: ID!) {
    volvoApiKey(id: $id) {
      ...VolvoApiKey
    }
  }
  ${CompanyFragments.volvoApiKey}
`

export const SCANIA_API_KEY = gql`
  query ScaniaApiKey($id: ID!) {
    scaniaApiKey(id: $id) {
      ...ScaniaApiKey
    }
  }
  ${CompanyFragments.scaniaApiKey}
`

export const ALL_DEPARTMENTS_QUERY = graphql(`
  query Departments {
    allDepartments {
      edges {
        node {
          id
          name
          externalId
        }
      }
    }
  }
`)

export const ALL_FACILITIES_QUERY = graphql(`
  query Facilities {
    allFacilities {
      edges {
        node {
          id
          name
          externalId
        }
      }
    }
  }
`)

export const ALL_HOLIDAY_ALLOWANCES_QUERY = graphql(`
  query AllHolidayAllowances {
    allHolidayAllowances {
      edges {
        node {
          id
          salaryCode {
            id
            name
            externalId
          }
          day
          start
          end
          override
          repeatAnnually
        }
      }
    }
  }
`)

export const ALL_SALARY_CODES_QUERY = graphql(`
  query SalaryCodes(
    $q: String
    $isDiet: Boolean
    $showInManualTimeEntryOvertime: Boolean
    $timeEntryCodes: Boolean
    $isSalaryDay: Boolean
    $isAddon: Boolean
    $userId: ID
  ) {
    allSalaryCodes(
      q: $q
      isDiet: $isDiet
      showInManualTimeEntryOvertime: $showInManualTimeEntryOvertime
      timeEntryCodes: $timeEntryCodes
      isSalaryDay: $isSalaryDay
      isAddon: $isAddon
      userId: $userId
    ) {
      edges {
        node {
          id
          name
          externalId
          useForAutoDietGeneration
          isDiet
          isSalaryDay
          rate
          showInManualTimeEntryOvertime
          showInTimeEntries
        }
      }
    }
  }
`)

export const COMPANY_DRIVER_CARDS_QUERY = gql`
  query CompanyDriverCards {
    companyDriverCards(isExpired: true) {
      edges {
        node {
          id
          expiresAt
          user {
            id
            fullName
          }
        }
      }
    }
  }
`

export const ALL_LEADER_GROUPS_QUERY = graphql(`
  query AllLeaderGroups {
    allLeaderGroups {
      id
      name
      usersCount
      excludedModules
      excludedModuleConfigs {
        edges {
          node {
            id
            name
          }
        }
      }
      leaders {
        id
        fullName
      }
      users {
        id
        fullName
      }
      salaryGroup {
        id
        name
      }
    }
  }
`)

export const ALL_COMPANY_VEHICLE_GROUPS_QUERY = graphql(`
  query AllCompanyVehicleGroups {
    allCompanyVehicleGroups {
      id
      name
      usersCount
      users {
        id
        fullName
      }
      vehicles {
        id
        vin
        registrationNumber
      }
    }
  }
`)
